import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Import locale files
import shopperBilingual from '../locales/shopper_bilingual_expanded.json';
import shopperUIPack from '../locales/shopper_bilingual_ui_pack.json';
import retailerTranslations from '../locales/retailer_translations.json';
import consumerTranslations from '../locales/consumer_translations.json';

/**
 * MULTILINGUAL ARCHITECTURE
 * 
 * Supports unlimited languages via ISO 639-1 codes: en, es, fr, de, pt, zh, etc.
 * 
 * Translation file format:
 * {
 *   "key": { "en": "English", "es": "Spanish", "fr": "French" }
 * }
 * 
 * Legacy format (auto-converted):
 * {
 *   "key": { "english": "English", "spanish": "Spanish" }
 * }
 */

// Supported languages with metadata
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', rtl: false },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', rtl: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', rtl: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', rtl: false },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', rtl: false },
];

// Currently enabled languages (can be expanded as translations are added)
export const ENABLED_LANGUAGES = ['en', 'es', 'ar', 'zh', 'ko'];

// Legacy key mapping for backward compatibility
const LEGACY_KEY_MAP = {
  'english': 'en',
  'spanish': 'es',
  'french': 'fr',
  'german': 'de',
  'portuguese': 'pt',
  'chinese': 'zh',
  'japanese': 'ja',
  'korean': 'ko',
  'arabic': 'ar',
  'hindi': 'hi'
};

// Convert legacy translation format to multilingual format
const convertLegacyTranslation = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  // Check if this is a translation object (has english/spanish keys)
  if (obj.english !== undefined || obj.spanish !== undefined) {
    const converted = {};
    Object.keys(obj).forEach(key => {
      const isoCode = LEGACY_KEY_MAP[key] || key;
      converted[isoCode] = obj[key];
    });
    return converted;
  }
  
  // Recursively convert nested objects
  const result = {};
  Object.keys(obj).forEach(key => {
    result[key] = convertLegacyTranslation(obj[key]);
  });
  return result;
};

// Merge and convert locale files
const mergeLocales = (...locales) => {
  const merged = {};
  locales.forEach(locale => {
    const converted = convertLegacyTranslation(locale);
    Object.keys(converted).forEach(section => {
      if (typeof converted[section] === 'object' && !converted[section].en && !converted[section].es) {
        // Nested section
        merged[section] = { ...merged[section], ...converted[section] };
      } else {
        // Direct translation or flat section
        merged[section] = { ...merged[section], ...converted[section] };
      }
    });
  });
  return merged;
};

const allLocales = mergeLocales(
  shopperBilingual, 
  shopperUIPack, 
  retailerTranslations, 
  consumerTranslations
);

// Backward compatibility exports
export const LANGUAGES = {
  ENGLISH: 'en',
  SPANISH: 'es',
  FRENCH: 'fr',
  GERMAN: 'de',
  PORTUGUESE: 'pt',
  CHINESE: 'zh',
  JAPANESE: 'ja',
  KOREAN: 'ko',
  ARABIC: 'ar',
  HINDI: 'hi'
};

// Language context
const LanguageContext = createContext(null);

// Storage key
const LANGUAGE_STORAGE_KEY = 'dealshaq_language';

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState('en');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved language preference on mount
  useEffect(() => {
    const savedLocale = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLocale) {
      // Handle legacy format (english/spanish) -> ISO codes
      const isoCode = LEGACY_KEY_MAP[savedLocale] || savedLocale;
      if (ENABLED_LANGUAGES.includes(isoCode)) {
        setLocaleState(isoCode);
      }
    }
    setIsLoaded(true);
  }, []);

  // Set language with validation
  const setLanguage = useCallback((code) => {
    // Handle legacy format
    const isoCode = LEGACY_KEY_MAP[code] || code;
    if (ENABLED_LANGUAGES.includes(isoCode)) {
      setLocaleState(isoCode);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, isoCode);
    }
  }, []);

  // Cycle to next language (useful for quick toggle)
  const cycleLanguage = useCallback(() => {
    const currentIndex = ENABLED_LANGUAGES.indexOf(locale);
    const nextIndex = (currentIndex + 1) % ENABLED_LANGUAGES.length;
    setLanguage(ENABLED_LANGUAGES[nextIndex]);
  }, [locale, setLanguage]);

  // Toggle between first two languages (backward compatible)
  const toggleLanguage = useCallback(() => {
    const newLocale = locale === 'en' ? 'es' : 'en';
    setLanguage(newLocale);
  }, [locale, setLanguage]);

  // Get translation function
  const t = useCallback((key, fallback = null) => {
    // Key format: "section.subsection.key" e.g., "consumer.nav.browse"
    const parts = key.split('.');
    
    let current = allLocales;
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        // Key not found, return fallback or key
        return fallback || key;
      }
    }
    
    // At the final level, get the locale value
    if (current && typeof current === 'object') {
      // Try current locale, then English fallback, then first available
      return current[locale] || current['en'] || Object.values(current)[0] || fallback || key;
    }
    
    return current || fallback || key;
  }, [locale]);

  // Get all translations for a key (useful for debugging)
  const tAll = useCallback((key) => {
    const parts = key.split('.');
    
    let current = allLocales;
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return { en: key };
      }
    }
    
    return current || { en: key };
  }, []);

  // Get language metadata
  const getLanguageInfo = useCallback((code = locale) => {
    return SUPPORTED_LANGUAGES.find(l => l.code === code) || SUPPORTED_LANGUAGES[0];
  }, [locale]);

  // Check if current language is RTL
  const isRTL = useCallback(() => {
    const langInfo = getLanguageInfo();
    return langInfo?.rtl || false;
  }, [getLanguageInfo]);

  const value = {
    // Current state
    locale,
    language: locale, // Backward compatibility alias
    
    // Setters
    setLanguage,
    setLocale: setLanguage, // Alias
    toggleLanguage,
    cycleLanguage,
    
    // Convenience booleans (backward compatible)
    isSpanish: locale === 'es',
    isEnglish: locale === 'en',
    isFrench: locale === 'fr',
    isGerman: locale === 'de',
    
    // Translation functions
    t,
    tAll,
    
    // Language utilities
    getLanguageInfo,
    isRTL,
    isLoaded,
    
    // Constants
    LANGUAGES,
    SUPPORTED_LANGUAGES,
    ENABLED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// HOC for class components
export function withLanguage(Component) {
  return function WrappedComponent(props) {
    const languageContext = useLanguage();
    return <Component {...props} {...languageContext} />;
  };
}

export default LanguageContext;
